import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
	autoUpdate,
	flip,
	FloatingElement,
	offset,
	ReferenceElement,
	shift,
	useFloating,
} from '@floating-ui/react-dom';
import cn from 'classnames';

import DynamicIconWrapper from '@components/common/DynamicIconWrapper/DynamicIconWrapper';
import { THandleChange } from '@components/common/Select/Select';

import { Icons } from '@icons/icons';

import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { TSelectOptionType } from '@components/common/Select/Select.types';

import colors from '@constants/colors';

interface ItemActionsDropdownProps {
	isSelected: boolean;
	isEditable: boolean;
	isRenamable: boolean;
	data: TSelectOptionType;
	onEditClick?: THandleChange;
	onRenameClick?: THandleChange;
	onDeleteClick?: THandleChange;
	handleEdit: () => void;
	handleRename: () => void;
	handleDelete: () => void;
	isActionsDropdownOpen: boolean;
	handleToggleActionsDropdown: (id: string | null) => void;
}

const ItemActionsDropdown = ({
	isSelected,
	isEditable,
	isRenamable,
	data,
	onEditClick,
	onRenameClick,
	onDeleteClick,
	handleEdit,
	handleRename,
	handleDelete,
	isActionsDropdownOpen,
	handleToggleActionsDropdown,
}: ItemActionsDropdownProps) => {
	const triggerRef = useRef<HTMLDivElement | null>(null) as MutableRefObject<HTMLDivElement | null>;

	const { refs, floatingStyles, update } = useFloating({
		placement: 'bottom-start',
		middleware: [offset(4), flip(), shift()],
	});

	const setRefs = useCallback((node: HTMLDivElement | null) => {
		if (!node) {
			return;
		}

		triggerRef.current = node;
		refs.setReference(node);
	}, [refs]);

	const setupFloatingAutoUpdateOnDropdownOpen = (
		isOpen: boolean,
		reference: ReferenceElement | null,
		floating: FloatingElement | null,
		update: () => void,
	) => {
		if (isOpen && reference && floating) {
			return autoUpdate(reference, floating, update);
		}
	};

	useEffect(() => {
		return setupFloatingAutoUpdateOnDropdownOpen(
			isActionsDropdownOpen,
			triggerRef.current,
			refs.floating.current,
			update,
		);
	}, [isActionsDropdownOpen, update, refs.floating]);

	useOnClickOutside({
		handler: () => handleToggleActionsDropdown(null),
		refs: [triggerRef, refs.floating],
		disableClickOutside: !isActionsDropdownOpen,
	});

	const actionsList = useMemo(() => [
		{
			title: 'Rename',
			isVisible: isRenamable && onRenameClick,
			onClick: handleRename,
		},
		{
			title: 'Edit',
			isVisible: isEditable && onEditClick,
			onClick: handleEdit,
		},
		{
			title: 'Delete',
			isVisible: !!onDeleteClick,
			onClick: handleDelete,
		},
	], [
		handleDelete,
		handleEdit,
		handleRename,
		isEditable,
		isRenamable,
		onDeleteClick,
		onEditClick,
		onRenameClick,
	]);

	const openDropdown = () => {
		if (!data?.id) {
			return;
		}

		handleToggleActionsDropdown(data.id.toString());
	};

	const handleActionItemClick = useCallback(
		(item: { onClick: () => void }) =>
			(e: React.MouseEvent<HTMLDivElement>) => {
				e.stopPropagation();
				item.onClick();
				handleToggleActionsDropdown(null);
			}, [handleToggleActionsDropdown]);

	return (
		<div
			className={cn('f5gEditableSelectActionsDropdown', {
				topOffset: !!data?.subText,
			})}
		>
			<div
				ref={setRefs}
				className='actionIcon'
				onClick={openDropdown}
			>
				<DynamicIconWrapper
					icon={Icons.actions}
					iconStyle={{
						color: isSelected ? colors.white : colors.indigo70,
					}}
				/>
			</div>
			{isActionsDropdownOpen && createPortal(
				<div
					ref={refs.setFloating}
					className='dropdownListSelectActions'
					style={floatingStyles}
				>
					{actionsList.map((item) => {
							return item.isVisible && (
								<div
									key={item.title}
									className='actionItemSelect'
									onClick={handleActionItemClick(item)}
								>
									{item.title}
								</div>
							);
						},
					)}
				</div>,
				document.body,
			)}
		</div>
	);
};

export default ItemActionsDropdown;
